import React, { useEffect, useState } from "react";
import { any, reject, mapObjIndexed, values } from "ramda";

import { combineValidations, presence, formatOfIf, exclusionFromIf } from "../../lib/validations";
import { isBlank, isPresent } from "../../lib/presence";

import Layout from "../Layout";
import Field from "./Field";
import Select from "./Select";
import * as UI from "./ui";
import countries from "../../lib/countries";

const INITIAL_REQUEST = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  "address.line1": "",
  "address.line2": "",
  "address.city": "",
  "address.state": "",
  "address.postalCode": "",
  "address.country": "US",
}

const donorStateValidations = combineValidations([
  presence,
  formatOfIf({
    pattern: /^[A-Za-z]{2}$/,
    message: "Please enter a valid 2 digit State or Province code.",
    predicate: context => context["address.country"] === "US",
  }),
  exclusionFromIf(
    ["WA"],
    "Not available to residents of the State of Washington.",
    context => context["address.country"] === "US",
  ),
]);

const validations = {
  firstName: presence,
  lastName: presence,
  email: presence,
  phoneNumber: presence,
  'address.line1': presence,
  'address.city': presence,
  'address.state': donorStateValidations,
  'address.postalCode': presence,
  'address.country': presence,
}

const validate = (values, validations) =>
  reject(isBlank,
    mapObjIndexed((validation, id) =>
      validation && validation(values[id], values), validations)
  )

const buildSubmissionObject = (values) => ({
  firstName: values.firstName,
  lastName: values.lastName,
  email: values.email,
  phoneNumber: values.phoneNumber,
  address: {
    line1: values["address.line1"],
    line2: values["address.line2"],
    city: values["address.city"],
    state: values["address.state"],
    postalCode: values["address.postalCode"],
    country: values["address.country"],
  }
})

const Form = ({ disabled, onSubmit }) => {
  const [ mailinTicketRequest, set ] = useState(INITIAL_REQUEST);
  const [formErrors, setFormErrors] = useState({});
  const [showAllErrors, setShowAllErrors] = useState(false);

  const validateForm = () => { setFormErrors(validate(mailinTicketRequest, validations)) }
  useEffect(validateForm, [mailinTicketRequest]);

  const handleSubmit = e => {
    e.preventDefault();

    if (any(isPresent, values(formErrors))) {
      setShowAllErrors(true)
      return;
    }

    onSubmit(buildSubmissionObject(mailinTicketRequest));
  };

  const setField = id => e => {
    const value = e.target.value;

    set(current => ({
      ...current,
      [id]: value,
    }));
  };

  return (
    <Layout.FlexibleColumn span={[7,15]} breakpoint="belowLaptop">
      <UI.Form onSubmit={handleSubmit}>
        <UI.FieldGroup>
          <Field label="First Name" onChange={setField("firstName")} value={mailinTicketRequest.firstName}  errors={formErrors.firstName} touched={showAllErrors} />
          <Field label="Last Name" onChange={setField("lastName")} value={mailinTicketRequest.lastName} errors={formErrors.lastName} touched={showAllErrors} />
        </UI.FieldGroup>
        <Field type="email" label="Email" onChange={setField("email")} value={mailinTicketRequest.email} errors={formErrors.email} touched={showAllErrors} />
        <Field type="tel" label="Phone" onChange={setField("phoneNumber")} value={mailinTicketRequest.phoneNumber} errors={formErrors.phoneNumber} touched={showAllErrors} />
        <UI.FieldGroup>
          <Field label="Address" onChange={setField("address.line1")} value={mailinTicketRequest["address.line1"]} weight={3} errors={formErrors['address.line1']} touched={showAllErrors} />
          <Field label="Apt #" onChange={setField("address.line2")} value={mailinTicketRequest["address.line2"]} weight={2} />
        </UI.FieldGroup>
        <UI.FieldGroup>
          <Field label="City" onChange={setField("address.city")} value={mailinTicketRequest["address.city"]} weight={2} errors={formErrors['address.city']} touched={showAllErrors}/>
          <Field label="State" onChange={setField("address.state")} value={mailinTicketRequest["address.state"]} errors={formErrors['address.state']} touched={showAllErrors} maxLength={2} persistErrors />
          <Field label="Zip / Postal Code" onChange={setField("address.postalCode")} value={mailinTicketRequest["address.postalCode"]} errors={formErrors['address.postalCode']} touched={showAllErrors}/>
        </UI.FieldGroup>
        <Select label="Country" onChange={setField("address.country")} value={mailinTicketRequest["address.country"]} options={countries} errors={formErrors['address.country']} touched={showAllErrors}/>
        <UI.Legal>* By continuing, I certify I am 18 years of age or older, eligible to participate in this sweepstakes, and I agree to abide by the sweepstakes rules.</UI.Legal>
        <UI.FieldGroup>
          <UI.ActionButton value="Submit Request" disabled={disabled} />
        </UI.FieldGroup>
      </UI.Form>
    </Layout.FlexibleColumn>
  );
};

export default Form;
